import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { loadingTrue, loadingFalse } from "../../../Reducer/loaderSlice";
import Handler from "../../../Helpers/Handler";
import Pagination from "../../../Helpers/Pagination";
import "../../../Assets/css/style.css";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";

const TABS = [
  { key: "all", label: "All", icon: "bx-list-ul" },
  { key: "today", label: "Today", icon: "bx-calendar-check" },
  { key: "upcoming", label: "Upcoming", icon: "bx-time-five" },
  { key: "past", label: "Past", icon: "bx-history" },
];

const STATUS_MAP = {
  SCHEDULED: { cls: "ms-scheduled", icon: "bx-calendar" },
  COMPLETED: { cls: "ms-completed", icon: "bx-check-circle" },
  CANCELLED: { cls: "ms-cancelled", icon: "bx-x-circle" },
};

const MeetingList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const [delId, setDelId] = useState(null); // confirm delete
  const limit = 9;

  const fetch = async () => {
    dispatch(loadingTrue());
    const res = await Handler({
      method: "GET",
      url: `/meetings/list.php?type=${tab}&q=${query}&page=${page}&limit=${limit}`,
    });
    dispatch(loadingFalse());
    if (res.success) {
      setList(res.data?.list || []);
      setTotal(res.data?.pagination?.total || 0);
    } else {
      toast.error(res.message || "Failed to load meetings");
    }
  };

  useEffect(() => { fetch(); }, [tab, query, page]);

  const handleTabChange = (key) => {
    setTab(key);
    setPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search);
  };

  const confirmDelete = (id) => setDelId(id);
  const cancelDelete = () => setDelId(null);

  const handleDelete = async () => {
    dispatch(loadingTrue());
    const res = await Handler({
      method: "POST",
      url: "/meetings/delete.php",
      data: { meeting_id: delId },
    });
    dispatch(loadingFalse());
    setDelId(null);
    if (res.success) {
      toast.success("Meeting deleted");
      fetch();
    } else {
      toast.error(res.message || "Delete failed");
    }
  };

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });

  const fmtTime = (d) =>
    new Date(d).toLocaleTimeString("en-IN", {
      hour: "2-digit", minute: "2-digit", hour12: true,
    });

  const isPast = (d) => new Date(d) < new Date();

  return (
    <MainLayout>
      <FeedLayout>
        <div className="mt-page">

          {/* Header */}
          <div className="mt-header">
            <div>
              <h1 className="mt-title">Meetings</h1>
              <p className="mt-subtitle">Schedule and track your meetings</p>
            </div>
            <div className="mt-header-actions">
              <button className="mt-btn-outline" onClick={() => navigate("/user/meeting-calendar")}>
                <i className="bx bx-calendar" /> Calendar
              </button>
              <button className="mt-btn-primary" onClick={() => navigate("/user/create-meeting")}>
                <i className="bx bx-plus" /> New Meeting
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`mt-tab ${tab === t.key ? "mt-tab-active" : ""}`}
                onClick={() => handleTabChange(t.key)}
              >
                <i className={`bx ${t.icon}`} /> {t.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <form className="mt-search-bar" onSubmit={handleSearch}>
            <i className="bx bx-search mt-search-icon" />
            <input
              className="mt-search-input"
              placeholder="Search by title, status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {query && (
              <button type="button" className="mt-clear-btn" onClick={() => { setSearch(""); setQuery(""); setPage(1); }}>
                <i className="bx bx-x" />
              </button>
            )}
            <button type="submit" className="mt-btn-search">Search</button>
          </form>

          {/* List */}
          {list.length === 0 ? (
            <div className="mt-empty">
              <i className="bx bx-calendar-x mt-empty-icon" />
              <p>No meetings found</p>
              <button className="mt-btn-primary" onClick={() => navigate("/user/create-meeting")}>
                Schedule a meeting
              </button>
            </div>
          ) : (
            <div className="mt-grid">
              {list.map((m) => {
                const s = STATUS_MAP[m.status] || STATUS_MAP.SCHEDULED;
                const past = isPast(m.meeting_date);
                return (
                  <div key={m.id} className={`mt-card ${past && m.status === "SCHEDULED" ? "mt-card-overdue" : ""}`}>
                    <div className="mt-card-top">
                      <span className={`mt-status-badge ${s.cls}`}>
                        <i className={`bx ${s.icon}`} /> {m.status}
                      </span>
                      {m.reminder_days_before > 0 && (
                        <span className="mt-reminder-chip">
                          <i className="bx bx-bell" /> {m.reminder_days_before}d reminder
                        </span>
                      )}
                    </div>

                    <h3 className="mt-card-title">{m.title}</h3>
                    {m.description && (
                      <p className="mt-card-desc">
                        {m.description.length > 85
                          ? m.description.substring(0, 85) + "..."
                          : m.description}
                      </p>
                    )}

                    <div className="mt-card-datetime">
                      <span><i className="bx bx-calendar-alt" /> {fmtDate(m.meeting_date)}</span>
                      <span><i className="bx bx-time" /> {fmtTime(m.meeting_date)}</span>
                    </div>

                    <div className="mt-card-actions">
                      {m.status !== "COMPLETED" && m.status !== "CANCELLED" && (
                        <button
                          className="mt-act-btn mt-act-edit"
                          onClick={() => navigate(`/user/edit-meeting/${m.id}`)}
                        >
                          <i className="bx bx-edit" /> Edit
                        </button>
                      )}
                      <button
                        className="mt-act-btn mt-act-delete"
                        onClick={() => confirmDelete(m.id)}
                      >
                        <i className="bx bx-trash" /> Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {total > limit && (
            <Pagination
              page={page}
              total={total}
              limit={limit}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => p + 1)}
            />
          )}

          {/* Delete confirm modal */}
          {delId && (
            <div className="mt-modal-overlay">
              <div className="mt-modal">
                <div className="mt-modal-icon">
                  <i className="bx bx-trash" />
                </div>
                <h4 className="mt-modal-title">Delete Meeting?</h4>
                <p className="mt-modal-text">This action cannot be undone.</p>
                <div className="mt-modal-actions">
                  <button className="mt-btn-outline" onClick={cancelDelete}>Cancel</button>
                  <button className="mt-btn-danger" onClick={handleDelete}>Delete</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </FeedLayout>
    </MainLayout>

  );
};

export default MeetingList;